/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DigitizePointService } from '../../../map/command/digitize-point/digitize-point.service';
import { ReportDefinition, ReportSubtypeValue, ReportTypeValue } from '../../state/report.model';
import { filter, map, mergeMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ClearReports, EnableTableEdit, SelectReportByName } from '../../state/report.actions';
import { Select, Store } from '@ngxs/store';
import { LoadDataForm } from '../../state/data-form.actions';
import { MatDialog } from '@angular/material/dialog';
import { DataFormDialogComponent } from '../../dataForm/data-form-dialog.component';
import { BehaviorSubject, combineLatest, iif, Observable, of, Subject } from 'rxjs';
import { LoggerService } from '../../../shared/services/logger.service';
import { ReportState } from '../../state/report.state';
import { IsolatedReportService } from '../../services/isolatedReport.service';
import { ReportService } from '../../services/report.service';
import { PageState } from '../../../../core/state/page/page.state';
import { GsptService } from '../../../../components/gspt/gspt.service';
import { LineIsolationService } from '../../../../components/isolation/line-isolation.service';
import { isEqual } from 'lodash-es';
import { ConfirmComponent } from '../../../../core/ui/confirm/confirm.component';
import { DeleteJob } from '../../../admin/job/state/job.actions';
import { ShowSuccess, ShowWarning } from '../../../../core/state/notification/notification.actions';
import { FlexDirective, LayoutDirective, LayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { UploadComponent } from './upload/upload.component';
import { ZoomButtonComponent } from '../../../../core/ui/zoomButton/zoom-button.component';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { LineIsolationComponent } from '../../../../components/isolation/line-isolation.component';
import { GsptComponent } from '../../../../components/gspt/gspt.component';
import { ReportPrintComponent } from './print/report-print.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'dash-gis-report-controls',
    templateUrl: './report-controls.component.html',
    styleUrls: ['./report-controls.component.scss'],
    imports: [
        FlexDirective,
        LayoutDirective,
        LayoutAlignDirective,
        UploadComponent,
        ZoomButtonComponent,
        MatIconButton,
        MatTooltip,
        MatIcon,
        LineIsolationComponent,
        GsptComponent,
        ReportPrintComponent,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        AsyncPipe,
    ],
})
export class ReportControlsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public definition: ReportDefinition;
    @Input() public keys: any[];
    @Input() public digitizeKey: string;

    // template observables
    public recordCount$: Observable<number>;
    public canZoom$: Observable<boolean>;
    public canTableEdit$: Observable<boolean>;
    public canEdit$: Observable<boolean>;
    public canMovePoint$: Observable<boolean>;
    public canDeletePoint$: Observable<boolean>;
    public shouldDisplay$: Observable<boolean>;

    mapEnabled$: Observable<boolean> = inject(Store).select(PageState.mapEnabled);

    public hasDefinition$ = new BehaviorSubject<boolean>(false);

    private mapName$ = new BehaviorSubject<string>(null);
    private layerName$ = new BehaviorSubject<string>(null);

    private destroy$ = new Subject<void>();

    private dataReportKey: any = null;

    constructor(
        private logger: LoggerService,
        private reportService: ReportService,
        private isolatedReportService: IsolatedReportService,
        private digitizeService: DigitizePointService,
        private dialog: MatDialog,
        private store: Store,
        private gspt: GsptService,
        private lineIsolation: LineIsolationService
    ) {}

    ngOnInit() {
        this.initObservables();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.definition) {
            const prev = changes.definition.previousValue;
            const current = changes.definition.currentValue;
            if (!isEqual(prev, current)) {
                this.hasDefinition$.next(!!current);
                this.mapName$.next(current?.map);
                this.layerName$.next(current?.layer);
            }
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
    }

    public movePoint() {
        this.store.dispatch(new ClearReports({ type: this.reportType }));
        this.digitizeService.movePoint(this.layer, this.digitizeKey);
    }

    public deletePoint() {
        this.dialog
            .open(ConfirmComponent, {
                data: {
                    message: 'Do you really want to delete this point?',
                    actionLabels: {
                        yes: 'Yes',
                        no: 'No',
                    },
                },
            })
            .afterClosed()
            .subscribe((shouldDelete) => {
                if (shouldDelete) {
                    this.store.dispatch(new ClearReports({ type: this.reportType }));
                    this.digitizeService.deletePoint(this.layer, this.digitizeKey);
                    this.store.dispatch(new ShowSuccess('Point deleted'));
                }
            });
    }

    public getDataFormKey$() {
        return this.isolatedReportService.dataSource.dataFormKeys$.pipe(filter((d) => d.length >= 1));
    }

    public launchDataForm() {
        this.getDataFormKey$()
            .pipe(
                take(1),
                switchMap((keys) =>
                    this.store.dispatch(
                        new LoadDataForm({
                            name: this.definition.dataFormName,
                            keys,
                            initialSelection: null,
                            preLaunchCallback: null,
                            postSaveCallback: null,
                        })
                    )
                ),
                switchMap(() =>
                    this.dialog
                        .open(DataFormDialogComponent, { data: { name: this.definition.dataFormName } })
                        .afterClosed()
                )
            )
            .subscribe((dataSaved: boolean) => {
                if (dataSaved) {
                    this.isolatedReportService.triggerReport();
                }
            });
    }

    public enableTableEdit() {
        this.store.dispatch(
            new EnableTableEdit({
                id: this.id,
                reportName: this.definition.name,
            })
        );
    }

    public setOtherReport(report: ReportDefinition) {
        this.store.dispatch(new SelectReportByName({ reportName: report.name }));
    }

    public downloadExcel() {
        this.isolatedReportService.triggerExcelDownload();
    }

    private initObservables() {
        this.isolatedReportService.hasDataSource$
            .pipe(
                filter(Boolean),
                tap(() => {
                    this.recordCount$ = this.isolatedReportService.dataSource?.count$;

                    this.canZoom$ = combineLatest([this.recordCount$, this.hasData$]).pipe(
                        map(([recordCount, hasData]) => this.definition?.enableZoom && hasData && recordCount === 1),
                        takeUntil(this.destroy$)
                    );

                    this.canTableEdit$ = combineLatest([
                        this.hasData$,
                        this.isVolt$,
                        this.isolatedReportService.dataSource.reportSubType$,
                    ]).pipe(
                        map(
                            ([hasData, isVolt, subType]) =>
                                !!this.definition?.tableDataFormName && !isVolt && hasData && subType === 'tabular'
                        ),
                        takeUntil(this.destroy$)
                    );

                    this.canEdit$ = this.hasData$.pipe(
                        map((hasData) => !!this.definition?.dataFormName && hasData),
                        takeUntil(this.destroy$)
                    );

                    this.canMovePoint$ = this.hasDefinition$.pipe(
                        mergeMap((hasDef) =>
                            iif(
                                () => hasDef,
                                this.digitizeService
                                    .canMovePoint$(this.definition?.layer)
                                    .pipe(map((canMove) => canMove && this.reportSubType === 'detail')),
                                of(false)
                            )
                        )
                    );

                    this.canDeletePoint$ = this.hasDefinition$.pipe(
                        mergeMap((hasDef) =>
                            iif(
                                () => hasDef,
                                this.digitizeService
                                    .canDeletePoint$(this.definition?.layer)
                                    .pipe(map((canDelete) => canDelete && this.reportSubType === 'detail')),
                                of(false)
                            )
                        )
                    );

                    const isLineIsolationEnabled$ = this.hasDefinition$.pipe(
                        filter((d) => !!d),
                        map(() => [this.definition?.map, this.definition?.layer]),
                        mergeMap(([mapName, layer]) => this.lineIsolation.isIsolationEnabled(mapName, layer)),
                        takeUntil(this.destroy$)
                    );
                    const isGsptEnabled$ = this.hasDefinition$.pipe(
                        filter((d) => !!d),
                        mergeMap(() => this.gspt.isGsptEnabled$(this.mapName$, this.layerName$)),
                        takeUntil(this.destroy$)
                    );

                    this.shouldDisplay$ = combineLatest([
                        this.hasDefinition$,
                        this.canZoom$,
                        this.canMovePoint$,
                        this.canDeletePoint$,
                        this.canTableEdit$,
                        this.canEdit$,
                        this.mapEnabled$,
                        isGsptEnabled$,
                        isLineIsolationEnabled$,
                    ]).pipe(
                        map(
                            ([
                                hasDefinition,
                                canZoom,
                                canMovePoint,
                                canDeletePoint,
                                canTableEdit,
                                canEdit,
                                mapEnabled,
                                isGsptEnabled,
                                isLineIsolationEnabled,
                            ]) =>
                                hasDefinition &&
                                (canZoom ||
                                    canMovePoint ||
                                    canDeletePoint ||
                                    canTableEdit ||
                                    canEdit ||
                                    (mapEnabled && isLineIsolationEnabled && isGsptEnabled) ||
                                    !this.definition.disablePrint ||
                                    this.definition.excelEnabled ||
                                    this.definition.otherReports.length > 0)
                        ),
                        takeUntil(this.destroy$)
                    );

                    combineLatest([this.canZoom$, this.isolatedReportService.dataSource.reportRawData$])
                        .pipe(
                            map(([canZoom, data]) => {
                                if (this.definition?.type === 'data' && data?.length > 0 && !this.keys && canZoom) {
                                    this.dataReportKey = data[0][this.definition.objectKey];
                                }
                            }),
                            takeUntil(this.destroy$)
                        )
                        .subscribe();
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    get id() {
        return this.isolatedReportService.id;
    }

    get otherReports() {
        const otherReports = [];
        if (this.definition && this.definition.otherReports) {
            const findDefFn = this.store.selectSnapshot(ReportState.reportByName);
            for (const other of this.definition.otherReports) {
                const def = findDefFn(other);
                if (def) {
                    otherReports.push(def);
                }
            }
        }
        return otherReports;
    }

    get excelEnabled(): boolean {
        return this.definition.excelEnabled;
    }

    get hasData$(): Observable<boolean> {
        return this.isolatedReportService.hasData$;
    }

    get isVolt$(): Observable<boolean> {
        return this.isolatedReportService.isVolt$;
    }

    get layer() {
        let layer = null;
        if (this.definition) {
            layer = this.definition.layer;
        }
        return layer;
    }

    get objectKey() {
        if (this.keys && this.keys.length === 1) {
            return this.keys[0];
        } else if (this.definition.type === 'data' && this.definition.subType === 'detail') {
            return this.isolatedReportService.objectKey;
        } else if (this.definition.type === 'data' && this.dataReportKey) {
            return this.dataReportKey;
        }
    }

    get reportType(): ReportTypeValue {
        return this.definition.type;
    }

    get reportSubType(): ReportSubtypeValue {
        return this.definition.subType;
    }
}
